import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Plus, Trash2, Save, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * TierGoalTemplatesManager — Coach Panel UI for setting per-tier default goals.
 * Each tier has one template = a list of goal rows. When an athlete on that
 * tier first opens their goals (with 0 goals), the template auto-applies.
 */

const TIERS = [
  { id: "free", label: "Free", color: "gray" },
  { id: "foundations", label: "Foundations", color: "blue" },
  { id: "accelerator", label: "Accelerator", color: "amber" },
  { id: "advanced_accelerator", label: "Advanced Accelerator", color: "orange" },
  { id: "elite", label: "Elite", color: "purple" },
  { id: "custom_coaching", label: "Custom Coaching", color: "rose" },
];

const GOAL_TYPES = [
  { id: "strength", label: "Strength", defaultUnit: "lbs", defaultDirection: "up" },
  { id: "speed", label: "Speed", defaultUnit: "sec", defaultDirection: "down" },
  { id: "stamina", label: "Stamina", defaultUnit: "sec", defaultDirection: "down" },
  { id: "suppleness", label: "Suppleness", defaultUnit: "in", defaultDirection: "up" },
  { id: "skill", label: "Skill", defaultUnit: "reps", defaultDirection: "up" },
  { id: "body_comp", label: "Body Comp", defaultUnit: "lbs", defaultDirection: "down" },
  { id: "custom", label: "Custom", defaultUnit: "reps", defaultDirection: "up" },
];

const blankRow = () => ({
  title: "",
  goal_type: "strength",
  unit: "lbs",
  direction: "up",
  default_start_value: "",
  default_target_value: "",
});

const TierGoalTemplatesManager = () => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [openTier, setOpenTier] = useState("foundations");
  const [drafts, setDrafts] = useState({}); // tier -> [rows]
  const [savingTier, setSavingTier] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/coach/tier-goal-templates`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTemplates(d.templates || {});
        // Hydrate drafts from server
        const draft = {};
        for (const tier of TIERS.map((t) => t.id)) {
          const tpl = d.templates?.[tier];
          draft[tier] = (tpl?.goals || []).map((g) => ({
            title: g.title || "",
            goal_type: g.goal_type || "strength",
            unit: g.unit || "lbs",
            direction: g.direction || "up",
            default_start_value: String(g.default_start_value ?? ""),
            default_target_value: String(g.default_target_value ?? ""),
          }));
        }
        setDrafts(draft);
      }
    } catch (e) {
      console.error("template fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (tier, idx, patch) => {
    setDrafts((d) => {
      const rows = [...(d[tier] || [])];
      rows[idx] = { ...rows[idx], ...patch };
      return { ...d, [tier]: rows };
    });
  };

  const addRow = (tier) => {
    setDrafts((d) => ({ ...d, [tier]: [...(d[tier] || []), blankRow()] }));
  };

  const removeRow = (tier, idx) => {
    setDrafts((d) => ({ ...d, [tier]: (d[tier] || []).filter((_, i) => i !== idx) }));
  };

  const onTypeChange = (tier, idx, newType) => {
    const meta = GOAL_TYPES.find((t) => t.id === newType);
    updateRow(tier, idx, {
      goal_type: newType,
      unit: meta?.defaultUnit || "lbs",
      direction: meta?.defaultDirection || "up",
    });
  };

  const saveTier = async (tier) => {
    const rows = drafts[tier] || [];
    // Validate
    for (const [i, r] of rows.entries()) {
      if (!r.title.trim()) return toast.error(`Row ${i + 1}: name required`);
      if (!r.default_start_value || !r.default_target_value) return toast.error(`Row ${i + 1}: both values required`);
      if (Number(r.default_start_value) === Number(r.default_target_value)) return toast.error(`Row ${i + 1}: target must differ from start`);
    }

    setSavingTier(tier);
    try {
      const payload = {
        goals: rows.map((r) => ({
          title: r.title.trim(),
          goal_type: r.goal_type,
          unit: (r.unit || "").trim() || "lbs",
          direction: r.direction,
          default_start_value: Number(r.default_start_value),
          default_target_value: Number(r.default_target_value),
        })),
      };
      const r = await fetch(`${API}/coach/tier-goal-templates/${tier}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Save failed");
      toast.success(`${TIERS.find((t) => t.id === tier)?.label} template saved`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingTier(null);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm("Apply tier templates to all existing athletes with no goals? This is one-way and won't overwrite existing goals.")) return;
    try {
      const r = await fetch(`${API}/admin/tier-goal-templates/backfill`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Backfill failed");
      const d = await r.json();
      toast.success(`Seeded ${d.users_seeded} athlete${d.users_seeded === 1 ? "" : "s"} with ${d.goals_created} goals`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading templates…</div>;
  }

  return (
    <div className="space-y-4" data-testid="tier-goal-templates-manager">
      <div className="bg-gradient-to-br from-amber-500/10 via-[#0F1729] to-[#0F1729] border border-amber-500/20 rounded-2xl p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-amber-500/20 p-2 rounded-lg">
            <Target className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white mb-1">Tier Goal Templates</h2>
            <p className="text-xs text-gray-400">
              Set default goals per subscription tier. New athletes (and existing ones with 0 goals) auto-receive these on first dashboard load. They can edit, delete, or add their own freely.
            </p>
          </div>
        </div>
        <button
          onClick={runBackfill}
          className="text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          data-testid="tier-goal-backfill-btn"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          Backfill existing athletes (one-time)
        </button>
      </div>

      <div className="space-y-3">
        {TIERS.map((tier) => {
          const isOpen = openTier === tier.id;
          const rows = drafts[tier.id] || [];
          const hasSaved = !!templates[tier.id];

          return (
            <div
              key={tier.id}
              className="bg-[#0F1729] border border-[#1E293B] rounded-xl overflow-hidden"
              data-testid={`tier-template-card-${tier.id}`}
            >
              <button
                onClick={() => setOpenTier(isOpen ? null : tier.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full bg-${tier.color}-400`} />
                  <span className="font-bold text-white">{tier.label}</span>
                  <span className="text-xs text-gray-500">
                    {hasSaved ? `${rows.length} goal${rows.length === 1 ? "" : "s"}` : "no template"}
                  </span>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="border-t border-[#1E293B]"
                >
                  <div className="p-4 space-y-2">
                    {rows.length === 0 ? (
                      <p className="text-center py-4 text-xs text-gray-500">
                        No template yet. Click "Add goal" to start.
                      </p>
                    ) : (
                      rows.map((row, i) => (
                        <TemplateRow
                          key={i}
                          row={row}
                          onChange={(patch) => updateRow(tier.id, i, patch)}
                          onTypeChange={(t) => onTypeChange(tier.id, i, t)}
                          onRemove={() => removeRow(tier.id, i)}
                        />
                      ))
                    )}
                  </div>

                  <div className="p-4 border-t border-[#1E293B] flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => addRow(tier.id)}
                      className="text-sm bg-[#1E293B] hover:bg-[#293548] text-white font-semibold px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                      data-testid={`tier-template-add-row-${tier.id}`}
                    >
                      <Plus className="w-4 h-4" />
                      Add goal
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={() => saveTier(tier.id)}
                      disabled={savingTier === tier.id}
                      className="text-sm bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                      data-testid={`tier-template-save-${tier.id}`}
                    >
                      <Save className="w-4 h-4" />
                      {savingTier === tier.id ? "Saving…" : "Save template"}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =================== TEMPLATE ROW ===================
const TemplateRow = ({ row, onChange, onTypeChange, onRemove }) => (
  <div className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg p-3 grid grid-cols-12 gap-2 items-start" data-testid="template-row">
    {/* Title */}
    <div className="col-span-12 sm:col-span-4">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Goal name</label>
      <input
        value={row.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="e.g., Bench Press"
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white placeholder-gray-500 focus:border-amber-500/70 focus:outline-none"
        maxLength={80}
      />
    </div>
    {/* Type */}
    <div className="col-span-6 sm:col-span-2">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Type</label>
      <select
        value={row.goal_type}
        onChange={(e) => onTypeChange(e.target.value)}
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white focus:border-amber-500/70 focus:outline-none"
      >
        {GOAL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
    </div>
    {/* Unit */}
    <div className="col-span-3 sm:col-span-1">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Unit</label>
      <input
        value={row.unit}
        onChange={(e) => onChange({ unit: e.target.value })}
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white focus:border-amber-500/70 focus:outline-none"
        maxLength={12}
      />
    </div>
    {/* Direction */}
    <div className="col-span-3 sm:col-span-1">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Dir</label>
      <select
        value={row.direction}
        onChange={(e) => onChange({ direction: e.target.value })}
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white focus:border-amber-500/70 focus:outline-none"
      >
        <option value="up">↑</option>
        <option value="down">↓</option>
      </select>
    </div>
    {/* Start */}
    <div className="col-span-5 sm:col-span-2">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Default start</label>
      <input
        type="number" step="0.1"
        value={row.default_start_value}
        onChange={(e) => onChange({ default_start_value: e.target.value })}
        placeholder="0"
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white focus:border-amber-500/70 focus:outline-none"
      />
    </div>
    {/* Target */}
    <div className="col-span-5 sm:col-span-1">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 block">Target</label>
      <input
        type="number" step="0.1"
        value={row.default_target_value}
        onChange={(e) => onChange({ default_target_value: e.target.value })}
        placeholder="0"
        className="w-full bg-[#0F1729] border border-[#1E293B] rounded-md px-2 py-1.5 text-sm text-white focus:border-amber-500/70 focus:outline-none"
      />
    </div>
    {/* Remove */}
    <div className="col-span-2 sm:col-span-1 flex items-end justify-end h-full pb-1">
      <button
        onClick={onRemove}
        className="text-gray-500 hover:text-rose-400 p-1.5 transition-colors"
        title="Remove"
        data-testid="template-row-remove"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

export default TierGoalTemplatesManager;
